import styled, { css, keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   FreeCreditsModal — Light Water redesign
   Frosted-white shell with aqua bloom, brand-aqua claim CTA, light frost
   timer pill, and a clean referral block (frost link pill, brand fill
   progress). All original named exports preserved.
   ═══════════════════════════════════════════════════════════════════════ */

/* ─── Keyframes ─── */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow:
      var(--shadow-cta),
      0 0 0 0 rgba(var(--color-brand-400-rgb), 0);
  }
  50% {
    box-shadow:
      var(--shadow-cta),
      0 0 0 6px rgba(var(--color-brand-400-rgb), 0.16);
  }
`;

const floatUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-80px) scale(1.4);
  }
`;

const confettiBurst = keyframes`
  0% {
    opacity: 1;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  100% {
    opacity: 0;
    transform: scale(1.6);
  }
`;

const slideInSection = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const countUp = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;

/* ─── Overlay & Container ─── */

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
  background:
    radial-gradient(
      circle at 50% 30%,
      rgba(var(--color-brand-400-rgb), 0.18),
      transparent 55%
    ),
    rgba(15, 47, 84, 0.32);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transition: opacity 220ms ease;
`;

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  max-height: 90dvh;
  overflow-y: auto;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);

  [data-theme="dark"] & {
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }
  isolation: isolate;
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transform: ${({ $isAnimating }) =>
    $isAnimating ? "scale(1)" : "scale(0.94) translateY(20px)"};
  transition:
    opacity 280ms ease,
    transform 280ms var(--ease-snappy);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  gap: 0;
`;

/* ─── Header ─── */

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 22px 6px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

/* Frosted-glass circle close button */
export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  /* Top-left specular */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalSubtitle = styled.p`
  margin: 0;
  padding: 0 22px 18px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: normal;
`;

/* ─── Daily Claim Section ─── */

export const ClaimSection = styled.div`
  position: relative;
  margin: 0 14px;
  padding: 22px 18px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -8px 18px rgba(var(--color-brand-200-rgb), 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  overflow: hidden;
  animation: ${slideInSection} 0.35s ease-out 0.1s both;

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.32);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.4),
      inset 0 -8px 18px rgba(var(--color-brand-700-rgb), 0.16);
  }

  /* Gentle aqua bloom from top */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      90% 60% at 50% 0%,
      rgba(var(--color-brand-300-rgb), 0.22) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const ClaimIconWrap = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  border: 1px solid var(--glass-edge-soft);
  box-shadow:
    var(--shadow-cta),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--glass-text-on-pill);

  svg {
    color: var(--glass-text-on-pill);
    filter: drop-shadow(0 1px 3px rgba(var(--color-brand-900-rgb), 0.3));
    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.5),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.32);
  }
`;

export const ClaimTitle = styled.h3`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
  text-align: center;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6.5px;
`;

export const ClaimDescription = styled.p`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  margin: 0;
  text-align: center;
  line-height: normal;
  max-width: 320px;
`;

export const ClaimButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 260px;
  min-height: 48px;
  padding: 12px 28px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  ${({ $canClaim }) =>
    $canClaim &&
    css`
      animation: ${pulseGlow} 2.4s ease-in-out infinite;
    `}

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    animation: none;
    background: var(--color-surface-2);
    color: var(--color-text-muted);
    text-shadow: none;
    border-color: var(--color-line);
    box-shadow: var(--shadow-micro);
  }

  [data-theme="dark"] & {
    border-color: transparent;

    &:disabled {
      border-color: rgba(var(--color-brand-400-rgb), 0.18);
      background: rgba(var(--color-brand-900-rgb), 0.4);
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
    }
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 35%,
      rgba(var(--glass-white-rgb), 0.35) 50%,
      transparent 65%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s ease-in-out infinite;
    pointer-events: none;

    ${({ $canClaim }) =>
      !$canClaim &&
      css`
        display: none;
      `}
  }
`;

export const TimerReveal = styled.div`
  display: grid;
  grid-template-rows: ${({ $visible }) => ($visible ? "1fr" : "0fr")};
  width: 100%;
  margin-top: ${({ $visible }) => ($visible ? "0" : "-14px")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    grid-template-rows 360ms var(--ease-snappy),
    margin-top 360ms var(--ease-snappy),
    opacity 260ms var(--ease-snappy);

  > div {
    overflow: hidden;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

export const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export const TimerBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-brand-mist);
  box-shadow: var(--shadow-micro);

  [data-theme="dark"] & {
    border-color: transparent;
    background: rgba(var(--color-brand-700-rgb), 0.22);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }
`;

export const TimerDigit = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

export const TimerSeparator = styled.span`
  color: var(--color-brand-600);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  opacity: 0.55;

  [data-theme="dark"] & {
    color: #ffffff;
    opacity: 0.45;
  }
`;

export const TimerLabel = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
`;

/* ─── Claim Success Animation ─── */

export const ClaimSuccessOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
`;

export const FloatingText = styled.span`
  position: absolute;
  font-size: 20px;
  font-weight: 800;
  font-family: var(--font-family-primary);
  color: var(--color-brand-600);
  text-shadow:
    0 0 10px rgba(var(--color-brand-400-rgb), 0.55),
    0 1px 2px rgba(var(--glass-white-rgb), 0.6);
  animation: ${floatUp} 1.2s ease-out forwards;
  animation-delay: ${({ $delay }) => $delay || "0s"};
  left: ${({ $x }) => $x || "50%"};
  top: ${({ $y }) => $y || "50%"};
`;

export const ConfettiBurst = styled.div`
  position: absolute;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(var(--color-brand-400-rgb), 0.4) 0%,
    rgba(var(--color-brand-300-rgb), 0.18) 40%,
    transparent 70%
  );
  animation: ${confettiBurst} 0.85s ease-out forwards;
`;

export const CreditBounce = styled.span`
  display: inline-flex;
  animation: ${countUp} 0.5s ease-out;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
`;

/* ─── Divider ─── */

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    rgba(var(--color-brand-300-rgb), 0) 0%,
    rgba(var(--color-brand-300-rgb), 0.32) 50%,
    rgba(var(--color-brand-300-rgb), 0) 100%
  );
  margin: 18px 22px;
`;

/* ─── Referral Section ─── */

export const ReferralSection = styled.div`
  position: relative;
  margin: 0 14px 14px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  box-shadow: var(--shadow-micro);
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${slideInSection} 0.35s ease-out 0.2s both;
`;

export const ReferralHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ReferralIconWrap = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--color-brand-mist);
  border: 1px solid var(--color-line);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-brand-700);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);

  svg {
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: rgba(var(--color-brand-700-rgb), 0.22);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    color: var(--color-brand-300);
  }
`;

export const ReferralInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const ReferralTitle = styled.h3`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.015em;
  margin: 0;
`;

export const ReferralDescription = styled.p`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  margin: 0;
`;

export const ReferralCapNote = styled.p`
  margin: 0;
  padding: 8px 12px;
  border-radius: 12px;
  background: rgba(255, 176, 46, 0.16);
  border: 1px solid rgba(255, 176, 46, 0.28);
  color: #c57a00;
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
`;

export const ReferralLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ReferralLink = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-input);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);

  span {
    color: var(--color-brand-700);
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.005em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.4);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);

    span {
      color: #ffffff;
    }
  }
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  svg {
    width: 16px;
    height: 16px;
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  ${({ $success }) =>
    $success &&
    css`
      color: #11a168;
      border-color: rgba(33, 189, 125, 0.45);
      background: rgba(33, 189, 125, 0.16);

      svg {
        color: #11a168;
      }
    `}
`;

export const InviteButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  padding: 0 20px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: var(--color-brand-mist);
  border: 1px solid var(--color-line);
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(var(--color-brand-900-rgb), 0.08);
`;

export const ProgressFill = styled.div`
  width: ${({ $progress }) => $progress}%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  box-shadow: 0 0 8px rgba(var(--color-brand-400-rgb), 0.4);
  transition: width 400ms ease;
`;

export const ProgressText = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;
